import {
  ArrowDownTrayIcon,
  ClockIcon,
  CreditCardIcon,
  TrashIcon,
  TruckIcon,
  UserIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import jsPDF from 'jspdf';
import React, { useEffect, useState } from 'react';
import apiService, { adminService } from '../../services/api';

const OrderDetailsDialog = ({ isOpen, onClose, orderId, onOrderDeleted }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingDeliveryDate, setEditingDeliveryDate] = useState(false);
  const [deliveryDateValue, setDeliveryDateValue] = useState('');
  const [deliveryTimeValue, setDeliveryTimeValue] = useState('');
  const [savingDeliveryDate, setSavingDeliveryDate] = useState(false);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!isOpen || !orderId) return;

      try {
        setLoading(true);
        setError(null);
        const response = await adminService.getOrderById(orderId);
        setOrder(response.data.order);
        if (response.data.order.expectedDeliveryDate) {
          const date = new Date(response.data.order.expectedDeliveryDate);
          setDeliveryDateValue(date.toISOString().split('T')[0]);
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');
          setDeliveryTimeValue(`${hours}:${minutes}`);
        } else {
          setDeliveryDateValue('');
          setDeliveryTimeValue('18:00');
        }
      } catch (err) {
        console.error('Failed to fetch order details:', err);
        setError('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [isOpen, orderId]);
  const handleFulfillmentRetry = async () => {
    if (!order?._id) return;
    try {
      const response = await apiService.retryFulfillment(order._id);
      setOrder(previous => ({ ...previous, fulfillment: response.data?.fulfillment || previous.fulfillment }));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to retry Shiprocket fulfillment');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'confirmed': 'bg-blue-100 text-blue-800',
      'designing': 'bg-purple-100 text-purple-800',
      'proof-sent': 'bg-orange-100 text-orange-800',
      'proof-approved': 'bg-green-100 text-[#008000]',
      'printing': 'bg-indigo-100 text-indigo-800',
      'quality-check': 'bg-blue-100 text-blue-800',
      'shipped': 'bg-green-100 text-[#008000]',
      'delivered': 'bg-green-100 text-[#008000]',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (date) => {
    if (!date) return 'Not set';
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatCurrencyForPDF = (amount) => {
    return `Rs. ${new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)}`;
  };

  const generatePDF = () => {
    if (!order) return;

    const doc = new jsPDF();
    let yPos = 10;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;

    // Header - Black and White only
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('ORDER INVOICE', pageWidth / 2, yPos, { align: 'center' });
    yPos += 6;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Kigo Prints', pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;

    // Divider line
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 6;

    // Order Information - Two columns
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Order Information', margin, yPos);
    yPos += 5;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Order Number: ${order.orderNumber || order._id}`, margin, yPos);
    doc.text(`Order Date: ${formatDate(order.createdAt)}`, margin, yPos + 4);
    doc.text(`Status: ${order.status.replace('-', ' ').toUpperCase()}`, margin + 90, yPos);
    if (order.isRush) {
      doc.text('Rush Order: Yes', margin + 90, yPos + 4);
    }
    yPos += 10;

    // Customer Information - Two columns
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Customer Information', margin, yPos);
    yPos += 5;

    if (order.user) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Name: ${order.user.name || 'N/A'}`, margin, yPos);
      doc.text(`Email: ${order.user.email || 'N/A'}`, margin, yPos + 4);
      doc.text(`Phone: ${order.user.phone || 'N/A'}`, margin + 90, yPos);
    }
    yPos += 10;

    // Shipping Address
    if (order.shippingAddress) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Shipping Address', margin, yPos);
      yPos += 5;

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      if (order.deliveryType === 'self-pickup' || order.shippingAddress.addressLine1 === 'Pick from store') {
        doc.text('Pick from store', margin, yPos);
        yPos += 6;
      } else {
        doc.text(`${order.shippingAddress.fullName || ''}`, margin, yPos);
        yPos += 4;
        doc.text(`${order.shippingAddress.addressLine1 || ''}`, margin, yPos);
        yPos += 4;
        if (order.shippingAddress.addressLine2) {
          doc.text(`${order.shippingAddress.addressLine2}`, margin, yPos);
          yPos += 4;
        }
        doc.text(`${order.shippingAddress.city || ''}, ${order.shippingAddress.state || ''} ${order.shippingAddress.pincode || ''}`, margin, yPos);
        yPos += 4;
        doc.text(`Phone: ${order.shippingAddress.phone || ''}`, margin, yPos);
        yPos += 6;
      }
    }

    // Order Items Section
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Order Items', margin, yPos);
    yPos += 5;

    if (order.items && order.items.length > 0) {
      // Table Header
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text('Product', margin, yPos);
      doc.text('Qty', margin + 80, yPos);
      doc.text('Unit Price', margin + 100, yPos);
      doc.text('Total', margin + 150, yPos);
      yPos += 4;

      // Header line
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 4;

      // Table Rows
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      order.items.forEach((item, index) => {
        if (yPos > pageHeight - 50) {
          return;
        }

        const productName = item.product?.name || 'Product Name';
        const quantity = item.quantity || 0;
        const unitPrice = item.unitPrice || 0;
        const totalPrice = item.totalPrice || 0;

        // Split long product names
        const lines = doc.splitTextToSize(productName, 70);
        doc.text(lines[0], margin, yPos);
        if (lines.length > 1) {
          doc.text(lines[1], margin, yPos + 3);
        }

        doc.text(quantity.toString(), margin + 80, yPos);
        doc.text(formatCurrencyForPDF(unitPrice), margin + 100, yPos);
        doc.setFont('helvetica', 'bold');
        doc.text(formatCurrencyForPDF(totalPrice), margin + 150, yPos);
        doc.setFont('helvetica', 'normal');

        yPos += lines.length > 1 ? 7 : 5;

        // Template info if available
        if (item.template) {
          doc.setFontSize(6);
          doc.text(`Template: ${item.template.name}`, margin + 3, yPos);
          yPos += 4;
        }
      });
    } else {
      doc.setFontSize(8);
      doc.text('No items found', margin, yPos);
      yPos += 5;
    }

    yPos += 5;

    // Pricing Details - Right aligned
    const pricingStartX = margin + 100;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('Pricing Details', pricingStartX, yPos);
    yPos += 5;

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    const subtotal = order.pricing?.total || 0;
    doc.text('Subtotal:', pricingStartX, yPos);
    doc.setFont('helvetica', 'bold');
    doc.text(formatCurrencyForPDF(subtotal), pageWidth - margin, yPos, { align: 'right' });
    yPos += 4;

    if (order.pricing?.discount?.amount > 0) {
      doc.setFont('helvetica', 'normal');
      doc.text('Discount:', pricingStartX, yPos);
      doc.setFont('helvetica', 'bold');
      doc.text(`-${formatCurrencyForPDF(order.pricing.discount.amount)}`, pageWidth - margin, yPos, { align: 'right' });
      yPos += 4;
    }

    if (order.pricing?.tax?.amount > 0) {
      doc.setFont('helvetica', 'normal');
      doc.text('Tax:', pricingStartX, yPos);
      doc.setFont('helvetica', 'bold');
      doc.text(formatCurrencyForPDF(order.pricing.tax.amount), pageWidth - margin, yPos, { align: 'right' });
      yPos += 4;
    }

    if (order.pricing?.shipping?.cost > 0) {
      doc.setFont('helvetica', 'normal');
      doc.text('Shipping:', pricingStartX, yPos);
      doc.setFont('helvetica', 'bold');
      doc.text(formatCurrencyForPDF(order.pricing.shipping.cost), pageWidth - margin, yPos, { align: 'right' });
      yPos += 4;
    }

    if (order.rushCharges > 0) {
      doc.setFont('helvetica', 'normal');
      doc.text('Rush Charges:', pricingStartX, yPos);
      doc.setFont('helvetica', 'bold');
      doc.text(formatCurrencyForPDF(order.rushCharges), pageWidth - margin, yPos, { align: 'right' });
      yPos += 4;
    }

    // Total
    yPos += 2;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.line(pricingStartX, yPos, pageWidth - margin, yPos);
    yPos += 4;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    const total = (order.pricing?.total || 0) + (order.rushCharges || 0);
    doc.text('Total:', pricingStartX, yPos);
    doc.setFontSize(10);
    doc.text(formatCurrencyForPDF(total), pageWidth - margin, yPos, { align: 'right' });
    yPos += 8;

    // Payment Information - Two columns
    if (order.payment && yPos < pageHeight - 30) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('Payment Information', margin, yPos);
      yPos += 5;

      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.text(`Payment Method: ${order.payment.method || 'N/A'}`, margin, yPos);
      doc.text(`Payment Status: ${order.payment.status?.toUpperCase() || 'N/A'}`, margin + 90, yPos);
      yPos += 4;
      if (order.payment.ccavenueTrackingId) {
        doc.text(`CCAvenue Transaction ID: ${order.payment.ccavenueTrackingId}`, margin, yPos);
        yPos += 4;
      } else if (order.payment.razorpayPaymentId) {
        doc.text(`Razorpay Payment ID: ${order.payment.razorpayPaymentId}`, margin, yPos);
        yPos += 4;
      }
      yPos += 3;
    }

    // Special Instructions
    if (order.specialInstructions && yPos < pageHeight - 20) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('Special Instructions', margin, yPos);
      yPos += 5;

      const instructions = doc.splitTextToSize(order.specialInstructions, pageWidth - (margin * 2));
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      instructions.forEach((line) => {
        if (yPos < pageHeight - 10) {
          doc.text(line, margin, yPos);
          yPos += 4;
        }
      });
    }

    // Footer
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(
      `Generated on ${new Date().toLocaleDateString('en-IN')}`,
      pageWidth / 2,
      pageHeight - 8,
      { align: 'center' }
    );

    // Save the PDF
    const fileName = `Order-${order.orderNumber || order._id}-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Dialog */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Order Details
              </h3>
              <button
                onClick={onClose}
                className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white px-4 pb-4 sm:p-6 sm:pt-0">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              </div>
            ) : error ? (
              <div className="text-center text-red-600 py-8">
                {error}
              </div>
            ) : order ? (
              <div className="space-y-6">
                {/* Order Summary */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-lg font-medium text-gray-900 mb-3">Order Information</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Order Number:</span>
                        <span className="font-medium">{order.orderNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                          {order.status.replace('-', ' ').toUpperCase()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Order Date:</span>
                        <span className="font-medium">{formatDate(order.createdAt)}</span>
                      </div>
                      {order.isRush && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Rush Order:</span>
                          <span className="text-orange-600 font-medium">Yes</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Estimated Delivery:</span>
                        {editingDeliveryDate ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            <input
                              type="date"
                              value={deliveryDateValue}
                              onChange={(e) => setDeliveryDateValue(e.target.value)}
                              className="px-2 py-1 text-sm border border-gray-300 rounded"
                              min={new Date().toISOString().split('T')[0]}
                            />
                            <input
                              type="time"
                              value={deliveryTimeValue}
                              onChange={(e) => setDeliveryTimeValue(e.target.value)}
                              className="px-2 py-1 text-sm border border-gray-300 rounded"
                            />
                            <button
                              onClick={async () => {
                                if (!deliveryDateValue || !deliveryTimeValue) {
                                  alert('Please select both date and time');
                                  return;
                                }
                                try {
                                  setSavingDeliveryDate(true);
                                  const [hours, minutes] = deliveryTimeValue.split(':');
                                  const dateTime = new Date(deliveryDateValue);
                                  dateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
                                  await adminService.updateOrderDeliveryDate(order._id, dateTime.toISOString());
                                  const response = await adminService.getOrderById(order._id);
                                  setOrder(response.data.order);
                                  setEditingDeliveryDate(false);
                                  alert('Delivery date and time updated successfully');
                                } catch (error) {
                                  console.error('Failed to update delivery date:', error);
                                  alert('Failed to update delivery date');
                                } finally {
                                  setSavingDeliveryDate(false);
                                }
                              }}
                              disabled={savingDeliveryDate}
                              className="px-2 py-1 text-xs bg-[#008000] text-white rounded hover:bg-[#008000] disabled:opacity-50"
                            >
                              {savingDeliveryDate ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={() => {
                                setEditingDeliveryDate(false);
                                if (order.expectedDeliveryDate) {
                                  const date = new Date(order.expectedDeliveryDate);
                                  setDeliveryDateValue(date.toISOString().split('T')[0]);
                                  const hours = date.getHours().toString().padStart(2, '0');
                                  const minutes = date.getMinutes().toString().padStart(2, '0');
                                  setDeliveryTimeValue(`${hours}:${minutes}`);
                                } else {
                                  setDeliveryDateValue('');
                                  setDeliveryTimeValue('18:00');
                                }
                              }}
                              className="px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {order.expectedDeliveryDate
                                ? formatDate(order.expectedDeliveryDate)
                                : 'Not set'}
                            </span>
                            <button
                              onClick={() => {
                                setEditingDeliveryDate(true);
                                if (!deliveryDateValue && order.expectedDeliveryDate) {
                                  const date = new Date(order.expectedDeliveryDate);
                                  setDeliveryDateValue(date.toISOString().split('T')[0]);
                                  const hours = date.getHours().toString().padStart(2, '0');
                                  const minutes = date.getMinutes().toString().padStart(2, '0');
                                  setDeliveryTimeValue(`${hours}:${minutes}`);
                                } else if (!deliveryDateValue) {
                                  const defaultDate = new Date();
                                  defaultDate.setDate(defaultDate.getDate() + 3);
                                  setDeliveryDateValue(defaultDate.toISOString().split('T')[0]);
                                  setDeliveryTimeValue('18:00');
                                }
                              }}
                              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              Edit
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-lg font-medium text-gray-900 mb-3">Customer Information</h4>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="font-medium">{order.user?.name}</span>
                      </div>
                      <div className="text-gray-600">{order.user?.email}</div>
                      <div className="text-gray-600">{order.user?.phone}</div>
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-3">Order Items</h4>
                  <div className="bg-gray-50 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Product
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Design
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quantity
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Unit Price
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {order.items?.map((item, index) => (
                          <tr key={index}>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {item.product?.name || 'Product Name'}
                                </div>
                                {item.template && (
                                  <div className="text-sm text-gray-500">
                                    Template: {item.template.name}
                                  </div>
                                )}
                                {item.customizations && Object.keys(item.customizations).length > 0 && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {Object.entries(item.customizations).map(([key, value]) => (
                                      <div key={key}>{key}: {value}</div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              {item.designFile ? (
                                <div className="space-y-2">
                                  <div className="flex items-center space-x-2">
                                    <a
                                      href={item.designFile.secure_url || item.designFile.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full hover:bg-blue-200 transition-colors"
                                    >
                                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a2 2 0 00-2.828-2.828l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a2 2 0 00-2.828-2.828z"></path>
                                      </svg>
                                      View Design
                                    </a>
                                    <span className="text-xs text-gray-500 uppercase">
                                      {item.designFile.format}
                                    </span>
                                    {item.designFile.bytes && (
                                      <span className="text-xs text-gray-400">
                                        {(item.designFile.bytes / 1024).toFixed(1)}KB
                                      </span>
                                    )}
                                  </div>
                                  {item.designFile.originalName && (
                                    <div className="text-xs text-gray-500 truncate max-w-32" title={item.designFile.originalName}>
                                      📁 {item.designFile.originalName}
                                    </div>
                                  )}
                                  {item.designFile.width && item.designFile.height && (
                                    <div className="text-xs text-gray-400">
                                      📐 {item.designFile.width}×{item.designFile.height}
                                    </div>
                                  )}
                                  {item.designFile.tags && item.designFile.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {item.designFile.tags.slice(0, 2).map((tag, tagIndex) => (
                                        <span key={tagIndex} className="inline-block px-1 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                          {tag}
                                        </span>
                                      ))}
                                      {item.designFile.tags.length > 2 && (
                                        <span className="text-xs text-gray-400">+{item.designFile.tags.length - 2}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">No design file</span>
                              )}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {item.quantity}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(item.unitPrice)}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {formatCurrency(item.totalPrice)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Order-Level Designs */}
                {order.cloudinaryDesigns && order.cloudinaryDesigns.length > 0 && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-3">Order Design Files</h4>
                    <div className="bg-gray-50 rounded-lg overflow-hidden">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                        {order.cloudinaryDesigns.map((design, index) => (
                          <div key={index} className="bg-white rounded-lg p-4 shadow-sm border">
                            <div className="flex items-center justify-between mb-2">
                              <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${design.designType === 'customer-upload' ? 'bg-blue-100 text-blue-800' :
                                design.designType === 'proof' ? 'bg-yellow-100 text-yellow-800' :
                                  design.designType === 'final-design' ? 'bg-green-100 text-[#008000]' :
                                    design.designType === 'revision' ? 'bg-purple-100 text-purple-800' :
                                      'bg-gray-100 text-gray-800'
                                }`}>
                                {design.designType.replace('-', ' ').toUpperCase()}
                              </span>
                              <span className="text-xs text-gray-500 uppercase">
                                {design.format}
                              </span>
                            </div>

                            <div className="space-y-2">
                              <a
                                href={design.secure_url || design.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-3 py-2 bg-blue-50 text-blue-700 text-sm font-medium rounded-md hover:bg-blue-100 transition-colors w-full justify-center"
                              >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a2 2 0 00-2.828-2.828l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a2 2 0 00-2.828-2.828z"></path>
                                </svg>
                                View Design
                              </a>

                              {design.originalName && (
                                <div className="text-xs text-gray-600 truncate" title={design.originalName}>
                                  📁 {design.originalName}
                                </div>
                              )}

                              {design.width && design.height && (
                                <div className="text-xs text-gray-500">
                                  📐 {design.width}×{design.height}
                                </div>
                              )}

                              {design.bytes && (
                                <div className="text-xs text-gray-500">
                                  💾 {(design.bytes / 1024).toFixed(1)}KB
                                </div>
                              )}

                              {design.notes && (
                                <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                                  💬 {design.notes}
                                </div>
                              )}

                              {design.tags && design.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {design.tags.slice(0, 3).map((tag, tagIndex) => (
                                    <span key={tagIndex} className="inline-block px-1 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                      {tag}
                                    </span>
                                  ))}
                                  {design.tags.length > 3 && (
                                    <span className="text-xs text-gray-400">+{design.tags.length - 3}</span>
                                  )}
                                </div>
                              )}

                              <div className="text-xs text-gray-400 pt-1">
                                {design.uploadedAt && new Date(design.uploadedAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Pricing */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-lg font-medium text-gray-900 mb-3">Pricing Details</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal:</span>
                        <span>{formatCurrency(order.pricing?.total || 0)}</span>
                      </div>
                      {order.pricing?.discount?.amount > 0 && (
                        <div className="flex justify-between text-[#008000]">
                          <span>Discount:</span>
                          <span>-{formatCurrency(order.pricing.discount.amount)}</span>
                        </div>
                      )}
                      {order.pricing?.tax?.amount > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tax:</span>
                          <span>{formatCurrency(order.pricing.tax.amount)}</span>
                        </div>
                      )}
                      {order.pricing?.shipping?.cost > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Shipping:</span>
                          <span>{formatCurrency(order.pricing.shipping.cost)}</span>
                        </div>
                      )}
                      {order.rushCharges > 0 && (
                        <div className="flex justify-between text-orange-600">
                          <span>Rush Charges:</span>
                          <span>{formatCurrency(order.rushCharges)}</span>
                        </div>
                      )}
                      <div className="border-t pt-2 flex justify-between font-bold text-lg">
                        <span>Total:</span>
                        <span>{formatCurrency((order.pricing?.total || 0) + (order.rushCharges || 0))}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-lg font-medium text-gray-900 mb-3">Payment & Shipping</h4>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <CreditCardIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-gray-600">Payment Method:</span>
                        <span className="ml-2 font-medium capitalize">{order.payment?.method}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Payment Status:</span>
                        <select
                          value={order.payment?.status || 'pending'}
                          onChange={async (e) => {
                            const newStatus = e.target.value;
                            const notes = prompt(`Update payment status to "${newStatus}". Add notes (optional):`);
                            if (notes !== null) {
                              try {
                                await adminService.updateOrderPaymentStatus(order._id, newStatus, notes || "");
                                const response = await adminService.getOrderById(order._id);
                                setOrder(response.data.order);
                                alert("Payment status updated successfully");
                              } catch (error) {
                                console.error("Failed to update payment status:", error);
                                alert("Failed to update payment status");
                                const response = await adminService.getOrderById(order._id);
                                setOrder(response.data.order);
                              }
                            } else {
                              const response = await adminService.getOrderById(order._id);
                              setOrder(response.data.order);
                            }
                          }}
                          className={`ml-2 px-3 py-1 text-xs font-medium rounded-full border-0 outline-none transition-colors ${order.payment?.status === 'paid'
                            ? 'bg-green-100 text-[#008000]'
                            : order.payment?.status === 'failed'
                              ? 'bg-red-100 text-red-800'
                              : order.payment?.status === 'refunded'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                        >
                          <option value="pending">Pending</option>
                          <option value="paid">Paid</option>
                          <option value="failed">Failed</option>
                          <option value="refunded">Refunded</option>
                        </select>
                      </div>
                      {(order.payment?.ccavenueTrackingId || order.payment?.razorpayPaymentId) && (
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-gray-600">
                            {order.payment.ccavenueTrackingId ? 'CCAvenue Transaction ID:' : 'Razorpay Payment ID:'}
                          </span>
                          <span className="font-medium text-gray-900 font-mono text-sm">
                            {order.payment.ccavenueTrackingId || order.payment.razorpayPaymentId}
                          </span>
                        </div>
                      )}
                      {order.fulfillment?.status && (
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-gray-600">Fulfillment:</span>
                          <span className="font-medium text-gray-900 capitalize">{order.fulfillment.status.replace(/_/g, ' ')}</span>
                        </div>
                      )}
                      {order.fulfillment?.shiprocketOrderId && (
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-gray-600">Shiprocket Order ID:</span>
                          <span className="font-medium text-gray-900 font-mono text-sm">{order.fulfillment.shiprocketOrderId}</span>
                        </div>
                      )}
                      {order.fulfillment?.shipmentId && (
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-gray-600">Shipment ID:</span>
                          <span className="font-medium text-gray-900 font-mono text-sm">{order.fulfillment.shipmentId}</span>
                        </div>
                      )}
                      {(order.fulfillment?.courier || order.tracking?.carrier) && (
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-gray-600">Courier:</span>
                          <span className="font-medium text-gray-900 text-sm">{order.fulfillment.courier || order.tracking.carrier}</span>
                        </div>
                      )}
                      {order.fulfillment?.awb && (
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-gray-600">AWB:</span>
                          <span className="font-medium text-gray-900 font-mono text-sm">{order.fulfillment.awb}</span>
                        </div>
                      )}
                      {(order.tracking?.trackingUrl || order.fulfillment?.awb) && (
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-gray-600">Tracking:</span>
                          <a
                            href={order.tracking?.trackingUrl || `https://shiprocket.co/tracking/${order.fulfillment.awb}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium underline flex items-center gap-1"
                          >
                            Track Shipment &rarr;
                          </a>
                        </div>
                      )}
                      {order.fulfillment?.labelUrl && (
                        <div className="mt-2">
                          <a
                            href={order.fulfillment.labelUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center w-full px-3 py-1.5 bg-emerald-600 text-white rounded text-xs font-semibold hover:bg-emerald-700 transition-colors gap-1"
                          >
                            <ArrowDownTrayIcon className="w-4 h-4" />
                            Download Shipping Label (PDF)
                          </a>
                        </div>
                      )}
                      {order.fulfillment?.status === 'failed' && order.fulfillment?.lastError && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                          <span className="font-semibold">Fulfillment Error: </span>
                          {order.fulfillment.lastError}
                        </div>
                      )}
                      {order.payment?.status === 'paid' && ['pending', 'failed'].includes(order.fulfillment?.status) && order.deliveryType !== 'self-pickup' && (
                        <button
                          type="button"
                          onClick={handleFulfillmentRetry}
                          className="mt-3 w-full rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                        >
                          Retry Shiprocket Fulfillment
                        </button>
                      )}
                      {order.shippingAddress && (
                        <div>
                          <div className="flex items-center mb-1">
                            <TruckIcon className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-gray-600">Shipping Address:</span>
                          </div>
                          <div className="text-sm text-gray-700 ml-6">
                            {order.deliveryType === 'self-pickup' || order.shippingAddress.addressLine1 === 'Pick from store' ? (
                              <div>Pick from store</div>
                            ) : (
                              <>
                                <div>{order.shippingAddress.fullName}</div>
                                <div>{order.shippingAddress.addressLine1}</div>
                                {order.shippingAddress.addressLine2 && (
                                  <div>{order.shippingAddress.addressLine2}</div>
                                )}
                                <div>
                                  {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.pincode}
                                </div>
                                <div>{order.shippingAddress.phone}</div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Status History */}
                {order.statusHistory && order.statusHistory.length > 0 && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-3">Status History</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="space-y-3">
                        {order.statusHistory.map((history, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <ClockIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(history.status)}`}>
                                  {history.status.replace('-', ' ').toUpperCase()}
                                </span>
                                <span className="text-sm text-gray-500">
                                  {formatDate(history.timestamp)}
                                </span>
                              </div>
                              {history.notes && (
                                <p className="text-sm text-gray-600 mt-1">{history.notes}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Special Instructions */}
                {order.specialInstructions && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-3">Special Instructions</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700">{order.specialInstructions}</p>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse sm:space-x-reverse sm:space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
            {order && (
              <>
                <button
                  type="button"
                  onClick={generatePDF}
                  className="w-full inline-flex justify-center items-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:w-auto sm:text-sm"
                >
                  <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                  Download PDF
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const confirmMessage = `Are you sure you want to delete order #${order.orderNumber || order._id}?\n\nThis action cannot be undone.`;
                    if (!window.confirm(confirmMessage)) {
                      return;
                    }

                    try {
                      await adminService.deleteOrder(order._id);
                      alert("Order deleted successfully");
                      if (onOrderDeleted) {
                        onOrderDeleted();
                      }
                      onClose();
                    } catch (error) {
                      console.error("Failed to delete order:", error);
                      alert("Failed to delete order");
                    }
                  }}
                  className="w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:w-auto sm:text-sm"
                >
                  <TrashIcon className="h-5 w-5 mr-2" />
                  Delete Order
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsDialog;
